import { Entity, EntityData, EntityHelper, EntityTreeData } from "@app/entity/model/entity";
import { DbCrudHelper, RowsAffected } from "@app/shared/services/db-crud-helper";
import { Observable, catchError, map, switchMap } from "rxjs";

import { Injectable } from "@angular/core";
import { ClareDbCollection } from "@app/shared/resources/resource-objectifier";
import { ClareWSCrudHelperService } from "@app/shared/services/clare-ws-crud-helper.service";
import { DataServiceError } from "@app/shared/services/data-service-error";
import { TSLoggingService } from "@app/shared/services/ts-logging.service";
import { EntityAncestor } from "../model/entity";
import { EntityTreeDbService } from "./entity-tree-db.service";

export type CreateEntityPayload = Omit<Entity, "id">;
export type EntityWithAncestors = Entity & { ancestors: number[] };

@Injectable({
    providedIn: "root",
})
export class EntityDbService {
    helper = new EntityHelper();
    crudHelperService: ClareWSCrudHelperService<Entity, EntityData>;
    constructor(
        protected tsLoggingService: TSLoggingService,
        protected crudHelper: DbCrudHelper,
        private entityTreeDbService: EntityTreeDbService
    ) {
        this.crudHelperService = new ClareWSCrudHelperService(tsLoggingService, crudHelper, this.helper);
    }

    /**
     * @description Fetches an entity from the database
     * @param {number} id the ID of the entity to fetch
     * @returns {Observable<{ entity: Entity }>} the entity
     */
    getEntityWithAncestor(id: number): Observable<{ entity: EntityWithAncestors }> {
        return this.get(id).pipe(
            switchMap(entity =>
                this.entityTreeDbService.getEntityAncestor(id).pipe(
                    map((entityAncestor: EntityAncestor) => ({
                        entity: {
                            ...entity,
                            ancestors: entityAncestor.ancestors,
                        },
                    }))
                )
            )
        );
    }

    /**
     * @description Fetches all sub-entities for the given entity ID
     * @param {number} parentId the ID of the top-level parent entity to retrieve
     * @returns { Observable<{ entities: ClareDbCollection<EntityWithAncestors> }>} a collection of found entities
     */
    getEntities(parentId: number): Observable<{ entities: ClareDbCollection<EntityWithAncestors> }> {
        return this.crudHelper.makeRequest<EntityTreeData[]>("GetEntityTreeData", { id: parentId }).pipe(
            map(entities =>
                entities.reduce((collection, ent) => {
                    collection[ent.id] = ent;
                    return collection;
                }, {} as Record<number, EntityTreeData>)
            ),
            map(entities => {
                const result = {} as Record<number, EntityWithAncestors>;
                Object.keys(entities).forEach(k => {
                    const ent = entities[k as unknown as number];
                    // fusion_id and folder_id don't exist on EntityTreeData but we're not using it anywhere anyways
                    const entityWithAncestors = Object.assign(Entity.fromDbObject({ ...ent, folder_id: null, fusion_id: null }), {
                        ancestors: ent.ancestors,
                    });
                    result[k] = entityWithAncestors;
                });
                return result;
            }),
            map(entities => ({ entities })),
            catchError(DataServiceError.handleError())
        );
    }

    /**
     * @description create entity
     * @param entity new data
     * @returns { Observable } new saved entity
     */
    createEntity(entity: Entity): Observable<Entity | null> {
        return this.crudHelperService
            .create({
                endpoint: "CreateEntity",
                data: entity,
                logData: { entity },
            })
            .pipe(map(result => this.helper.fromDbObject(result)));
    }

    /**
     * @description get entity by id
     * @param id entity id
     * @returns { Observable } entity data
     */
    get(id: number, errorsToSuppress: string[] = []): Observable<Entity> {
        return this.crudHelperService
            .retrieveSingle({
                endpoint: "GetEntity",
                data: { id },
                errorsToSuppress,
            })
            .pipe(map(result => this.helper.fromDbObject(result)));
    }

    /**
     * @description update entity
     * @param entity updated data
     * @returns { Observable } row affected number
     */
    updateEntity(entity: Entity): Observable<RowsAffected> {
        return this.crudHelperService.update({
            data: entity,
            logData: { entity },
            endpoint: "UpdateEntity",
        });
    }

    /**
     * @description delete entity
     * @param entity deleting entity
     * @returns { Observable } row affected number
     */
    deleteEntity(entity: Entity | { id: Entity["id"] }): Observable<RowsAffected> {
        return this.crudHelperService.delete({
            endpoint: "DeleteEntity",
            id: entity.id,
            logData: { entity },
        });
    }

    /**
     * @description fetches closest parent entity support info
     * @param entity entity to fetch parent of
     * @returns { Observable } partial entity with support info
     */
    getEntityParentSupportInfo(entity: Entity): Observable<Partial<Entity>> {
        return this.crudHelperService
            .retrieveSingle({
                endpoint: "GetParentEntitySupport",
                data: { id: entity.id },
            })
            .pipe(map(result => this.helper.fromDbObject(result)));
    }
}
